import * as React from 'react';
import styled from 'styled-components';
import { debounce, get } from 'lodash';

import {
	CardContent,
	TextField,
	SelectField,
	DeleteIcon,
	IconButton,
	DialpadIcon,
	CameraIcon,
	Button,
	Dialog,
	CircularProgress,
	InputAdornment,
	CheckIcon,
	ErrorIcon,
} from '../material-ui';
import { WithData, client } from '../apollo-utils';
import { CheckQRCodeQuery, TicketCategoriesQuery } from '../queries';
import { Dialpad } from './Dialpad';
import { Scanner } from './Scanner';

const StyledDialog = styled(Dialog)`
	& > div {
		overflow: visible;
	}
`;

const StatusIconWrapper = styled.div`
	position: absolute;
	top: 2.5rem;
	left: 0.75rem;
	cursor: pointer;
	svg {
		font-size: 1rem;
	}
`;

const StyledCard = styled(Button).attrs({
	variant: 'contained',
	component: 'div',
	disableRipple: true,
})`
	padding: 0 0 0 0.25rem !important;
	margin: 1rem 0;
	text-align: left;
	cursor: default !important;
	p {
		font-size: 0.75rem;
		margin-top: 0.25rem;
	}
	&:hover {
		background-color: ${(p) => p.theme.palette.grey[300]} !important;
	}
	&.completed {
		p {
			color: rgba(255, 255, 255, 0.5) !important;
		}
		&:hover {
			background-color: ${(p) => p.theme.palette.primary.main} !important;
		}
		div,
		input,
		div[role='button'] span {
			color: white;
		}
		label,
		svg {
			color: rgba(255, 255, 255, 0.5) !important;
		}
		div:hover > span > svg {
			color: white !important;
		}
		div::before,
		div:hover::before {
			border-bottom-color: rgba(255, 255, 255, 0.3) !important;
		}
		div::after {
			border-bottom-color: white !important;
		}
	}
`;

const StyledCardContent = styled(CardContent)`
	height: 6rem;
	padding-left: 2rem !important;
	display: flex;
	flex-direction: row;
`;

const InputWrapper = styled.div`
	height: 4rem;
	margin-right: 2rem;
	&:last-child {
		margin-right: 0.5rem;
	}
	&.select {
		min-width: 22rem;
		width: 22rem;
	}
`;

const StyledButton = styled(IconButton)`
	margin-top: 0.5rem !important;
`;

const LoadingIndicator = styled(CircularProgress).attrs({})`
	position: absolute;
	right: -4rem;
`;

const IconPlacholder = styled.span`
	width: 16px;
`;

export class QRCodeCard extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			dialpad: false,
			scanner: false,
			qrCode: props.qrCode || '',
			user: null,
			loading: false,
		};
		if (props.qrCode) this.qrCodeInputFinished();
		this.qrCodeInputFinished = debounce(this.qrCodeInputFinished, 500);
	}

	qrRef = React.createRef();

	focus = () =>
		this.props.cameraScannerEnabled
			? this.handleShowScanner()
			: this.qrRef.current && this.qrRef.current.focus();

	setCategoryRef = (node) => {
		this.categoryRef = node;
	};

	handleDelete = () => this.props.onDelete && this.props.onDelete(this.props.id);

	handleQRCodeChange = ({ target: { value } }) => {
		//Strip all non-numeric characters (padding by scanners)
		const qrCode = value.replace(/\D/g, '');
		this.setState({ qrCode, loading: true }, this.qrCodeInputFinished);
	};

	qrCodeInputFinished = async () => {
		const { cinemaId, onValueSet, id } = this.props;
		const { qrCode } = this.state;
		try {
			const { data } = await client.query({
				query: CheckQRCodeQuery,
				variables: { cinemaId, qrCode },
				errorPolicy: 'all',
				fetchPolicy: 'network-only',
			});
			if (data.user) {
				if (this.props.categorySelection && !this.props.category) this.categoryRef.focus();
				this.setState({ user: data.user, loading: false });
				if (onValueSet) onValueSet({ id, key: 'user', value: data.user });
			}
			if (data.voucherInstance) {
				const { user, voucherClass, ...voucherInstance } = data.voucherInstance;
				const voucher = { ...voucherClass, ...voucherInstance, voucherClassId: voucherClass.id };
				this.setState({ user, loading: false });
				if (onValueSet) {
					//onValueSet({ id, key: 'user', value: user });
					onValueSet({ id, key: 'voucher', value: voucher });
				}
			}
			if (!data.user && !data.voucherInstance) {
				this.setState({ user: null, loading: false });
				// if (onValueSet) {
				// 	onValueSet({ id, key: 'user', value: null });
				// 	onValueSet({ id, key: 'voucher', value: null });
				// }
			}
		} catch (e) {
			if (e.networkError) {
				this.setState({ loading: false });
			} else throw e;
		}
	};

	handleCategoryChange = ({ target: { value } }, ticketCategories) => {
		const { onValueSet, id } = this.props;
		const category = ticketCategories.find((c) => c.id === value);
		if (onValueSet) onValueSet({ key: 'category', value: category, id });
	};

	handleShowDialpad = () => this.setState({ dialpad: true });
	handleHideDialpad = () => this.setState({ dialpad: false });
	handleDialpadInput = (value) => {
		this.handleHideDialpad();
		this.handleQRCodeChange({ target: { value } });
	};

	handleShowScanner = () => this.setState({ scanner: true });
	handleHideScanner = () => this.setState({ scanner: false });
	handleScannerInput = (value) => {
		this.handleHideScanner();
		this.handleQRCodeChange({ target: { value } });
	};

	render = () => {
		const { qrCode, user, loading } = this.state;
		const coinBalance = get(user, 'bonusProgramMembership.coinBalance', 0);
		const { category, cinemaId, onDelete, categorySelection, cameraScannerEnabled } = this.props;
		const completed = user && (!categorySelection || category);
		return (
			<StyledCard
				className={completed ? 'completed' : ''}
				color={completed ? 'primary' : 'default'}
			>
				<StyledDialog open={this.state.dialpad} onClose={this.handleHideDialpad}>
					<Dialpad
						onSubmit={this.handleDialpadInput}
						defaultValue={qrCode}
						onClose={this.handleHideDialpad}
					/>
				</StyledDialog>
				{cameraScannerEnabled ? (
					<StyledDialog open={this.state.scanner} onClose={this.handleHideScanner}>
						<Scanner onSubmit={this.handleScannerInput} onClose={this.handleHideScanner} />
					</StyledDialog>
				) : null}
				<StyledCardContent className={categorySelection ? 'category-selection' : ''}>
					<InputWrapper>
						<StatusIconWrapper>
							{user ? (
								<CheckIcon className="statusIcon" />
							) : !qrCode || loading ? (
								<IconPlacholder />
							) : (
								<ErrorIcon className="statusIcon" />
							)}
						</StatusIconWrapper>
						<TextField
							// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{cameraScannerEnabled && (
											<IconButton
												onClick={this.handleShowScanner}
												style={{ height: 36, marginRight: -10 }}
											>
												<CameraIcon />
											</IconButton>
										)}
										<IconButton onClick={this.handleShowDialpad} style={{ height: 36 }}>
											<DialpadIcon />
										</IconButton>
									</InputAdornment>
								),
							}}
							helperText={
								user
									? `${user.name} (${coinBalance} Punkte)`
									: !qrCode || loading
									? ''
									: 'Unbekannter QR Code'
							}
							error={Boolean(!user && qrCode && !loading)}
							label="QR Code"
							value={qrCode}
							onChange={this.handleQRCodeChange}
							inputRef={this.qrRef}
						/>
					</InputWrapper>
					{categorySelection && (
						<InputWrapper className="select">
							<WithData
								query={TicketCategoriesQuery}
								// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
								variables={{ cinemaId }}
							>
								{({ cinema: { ticketCategories } }) => (
									<SelectField
										label="Ticket Kategorie"
										value={category && category.id}
										// eslint-disable-next-line react/jsx-no-bind
										onChange={(e) => this.handleCategoryChange(e, ticketCategories)}
										items={ticketCategories.map(({ name, subtext, bonusPoints, id }) => ({
											label: name + (subtext ? ` (${subtext})` : '') + ` | ${bonusPoints}`,
											key: id,
										}))}
										ref={this.setCategoryRef}
									/>
								)}
							</WithData>
						</InputWrapper>
					)}
					{onDelete && (
						<StyledButton onClick={this.handleDelete} component="div">
							<DeleteIcon />
						</StyledButton>
					)}
				</StyledCardContent>
				{loading && <LoadingIndicator />}
			</StyledCard>
		);
	};
}



// WEBPACK FOOTER //
// ./src/components/QRCodeCard.js